/**
 * Cliente do serviço Fábrica de Anúncios (services/fabrica-anuncios, 127.0.0.1:8104).
 * Same-origin: sessão S2 via nginx auth_request; escritas levam X-Token injetado só no servidor.
 * O browser nunca vê token nem chave de IA.
 */
import type { ConcorrenteParse, Projeto, Referencia, StatusServico } from './logic'

export class FabricaApiError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.name = 'FabricaApiError'
    this.status = status
  }
}

const BASE = '/api/fabrica'

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const resp = await fetch(BASE + path, { credentials: 'include', ...init })
  if (!resp.ok) {
    const corpo = (await resp.json().catch(() => null)) as { erro?: string; detail?: unknown; message?: string } | null
    const detail = typeof corpo?.detail === 'string' ? corpo.detail : undefined
    throw new FabricaApiError(resp.status, corpo?.erro || detail || corpo?.message || `Serviço da Fábrica respondeu HTTP ${resp.status}.`)
  }
  return resp.json() as Promise<T>
}

const json = (body: unknown, method = 'POST'): RequestInit => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
})

export const fetchStatus = () => api<StatusServico>('/status')

// ---- referências por marca
export const fetchReferencias = (marca?: string) =>
  api<{ ok: boolean; marcas: Array<{ slug: string; marca: string; fotos: number; textos: number }>; itens: Referencia[] }>(
    `/referencias${marca ? `?marca=${encodeURIComponent(marca)}` : ''}`,
  )

export function enviarReferencia(campos: { marca: string; tipo: 'foto' | 'texto'; nome: string; nota?: string; texto?: string; arquivo?: File }) {
  const fd = new FormData()
  fd.set('marca', campos.marca)
  fd.set('tipo', campos.tipo)
  fd.set('nome', campos.nome)
  fd.set('nota', campos.nota || '')
  fd.set('texto', campos.texto || '')
  if (campos.arquivo) fd.set('arquivo', campos.arquivo, campos.arquivo.name)
  return api<{ ok: boolean; item: Referencia }>('/referencias', { method: 'POST', body: fd })
}

export const importarAnuncioReferencia = (payload: { marca: string; mlb: string; conta: string; nota?: string }) =>
  api<{ ok: boolean; item: Referencia }>('/referencias/importar-anuncio', json(payload))

export const removerReferencia = (id: string) => api<{ ok: boolean }>(`/referencias/${id}`, { method: 'DELETE' })

export const urlReferencia = (id: string) => `${BASE}/referencias/${id}/arquivo`

// ---- projetos
export type ResumoProjeto = {
  id: string
  produto: string
  marca: string
  conta: string
  criado_em: string
  atualizado_em: string
  pesquisa: string
  proposta: string
  fotos_ok: number
  publicacao: string
  publicacao_shopee?: string
  canais?: Array<'ml' | 'shopee'>
  aprovados: number
}

export const fetchProjetos = () => api<{ ok: boolean; projetos: ResumoProjeto[] }>('/projetos')
export const fetchProjeto = (id: string) => api<{ ok: boolean; projeto: Projeto; em_andamento: string[] }>(`/projetos/${id}`)
export const criarProjeto = (produto: Projeto['produto']) => api<{ ok: boolean; projeto: Projeto }>('/projetos', json({ produto }))
export const editarProjeto = (id: string, payload: Record<string, unknown>) =>
  api<{ ok: boolean; projeto: Projeto }>(`/projetos/${id}`, json(payload, 'PUT'))
export const apagarProjeto = (id: string) => api<{ ok: boolean }>(`/projetos/${id}`, { method: 'DELETE' })

export function enviarFotosReais(id: string, arquivos: File[]) {
  const fd = new FormData()
  for (const a of arquivos) fd.append('arquivos', a, a.name)
  return api<{ ok: boolean; projeto: Projeto; aceitas?: number; recusados?: Array<{ nome: string; motivo: string }> }>(`/projetos/${id}/fotos-reais`, { method: 'POST', body: fd })
}
export const urlFotoReal = (id: string, arquivo: string) => `${BASE}/projetos/${id}/fotos-reais/${arquivo}`
export const urlFotoGerada = (id: string, n: number, v?: string) => `${BASE}/projetos/${id}/foto/${n}${v ? `?v=${encodeURIComponent(v)}` : ''}`

/** Pesquisa v2: `soCampeoes` reaproveita categoria/ranking/CMV e refaz só os campeões (depois de colar links). */
export const dispararPesquisa = (id: string, soCampeoes = false) =>
  api<{ ok: boolean; mensagem: string }>(`/projetos/${id}/pesquisar`, json(soCampeoes ? { so_campeoes: true } : {}))
/** Salva os links de campeões (1 por linha, ML ou Shopee) e devolve o parse linha a linha. */
export const salvarConcorrentes = (id: string, texto: string, modo?: 'auto' | 'links') =>
  api<{ ok: boolean; projeto: Projeto; parse: ConcorrenteParse[]; validos: number }>(`/projetos/${id}/concorrentes`, json({ texto, modo_pesquisa: modo }))
export const urlGradeCampeoes = (id: string, v?: string) => `${BASE}/projetos/${id}/grade-campeoes.jpg${v ? `?v=${encodeURIComponent(v)}` : ''}`
export const urlRelatorioPesquisa = (id: string) => `${BASE}/projetos/${id}/relatorio-pesquisa`
export const dispararProposta = (id: string) => api<{ ok: boolean; mensagem: string }>(`/projetos/${id}/proposta`, { method: 'POST' })
export const calcularPrecos = (id: string, precos: number[]) => api<{ ok: boolean; precos: Projeto['precos'] }>(`/projetos/${id}/precos`, json({ precos }))
export const dispararFotos = (id: string, numeros?: number[]) =>
  api<{ ok: boolean; mensagem: string }>(`/projetos/${id}/gerar-fotos`, json(numeros ? { numeros } : {}))
export const previaPublicacao = (id: string) =>
  api<{ ok: boolean; pendencias: string[]; corpo: Record<string, unknown> }>(`/projetos/${id}/previa-publicacao`, { method: 'POST' })
/** ESCRITA NO ML — só chamar depois da confirmação na tela. O anúncio nasce PAUSADO. */
export const publicarPausado = (id: string) =>
  api<{ ok: boolean; item_id?: string; permalink?: string | null; pendencias?: string[]; validacao?: unknown }>(
    `/projetos/${id}/publicar`,
    json({ confirmo: 'PAUSADO' }),
  )

export const calcularPrecosShopee = (id: string, params: { taxa_pct?: number | ''; impostos_pct?: number | ''; mc_alvo_pct?: number; markup?: number; cmv?: number }) =>
  api<{ ok: boolean; precos_shopee: NonNullable<Projeto['precos_shopee']> }>(`/projetos/${id}/precos-shopee`, json(params))
export const previaShopee = (id: string) =>
  api<{ ok: boolean; pendencias: string[]; corpo: Record<string, unknown>; desconto: { preco_cheio?: number; preco_alvo?: number; desconto_pct?: number } }>(`/projetos/${id}/previa-shopee`, { method: 'POST' })
/** ESCRITA NA SHOPEE — só depois da confirmação na tela. O item nasce UNLIST (não listado). */
export const publicarShopee = (id: string) =>
  api<{ ok: boolean; item_id?: number; link?: string; pendencias?: string[]; desconto?: unknown }>(`/projetos/${id}/publicar-shopee`, json({ confirmo: 'SHOPEE' }))

export const fetchCmv = (q: { sku?: string; termo?: string }) => {
  const p = new URLSearchParams()
  if (q.sku) p.set('sku', q.sku)
  if (q.termo) p.set('termo', q.termo)
  return api<{ ok: boolean; encontrado?: boolean; cmv?: number; impostos_pct?: number; sku?: string; erro?: string }>(`/cmv?${p}`)
}
